// React
import { FunctionComponent, JSXElementConstructor, ReactElement, useCallback } from "react";
// Components
import { Page, PageConfiguration } from "@fyrstain/fhir-front-library";
import { MenuItem } from "@fyrstain/fhir-front-library";
// Translation
import i18n from "i18next";
// Authentication
import UserService from "../../services/UserService";
// Navigation
import { useNavigate } from "react-router-dom";
import { toPublicUrl } from "../../services/PublicUrl";

const PapyrusPage: FunctionComponent<{
    // The title of the page
    titleKey?: string;
    // The loading state of the page
    loading?: boolean;
    // The content of the page
    children?: ReactElement<any, string | JSXElementConstructor<any>> | undefined;
    // If the page needs login or not
    needsLogin: boolean;
}> = (props) => {

    /////////////////////////////////
    //        NAVIGATION           //
    /////////////////////////////////

    const navigate = useNavigate();

    /////////////////////////////////
    //           METHODS           //
    /////////////////////////////////

    /*
    **
    **  This function is used to handle the login of the user.
    **  If the user is not logged in and the menu item needs login, it will redirect to the login page.
    **
    */
    const handleLogin = useCallback((config: MenuItem) => {
        if (config.needsLogin) {
            if (!UserService.isAuthenticated()) {
                UserService.doLogin();
                navigate('/login');
            }
        }
    }, [navigate]);

    const handleLangChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        i18n.changeLanguage(event.target.value);
    };

    const fullConfig: PageConfiguration = {
        // Translation
        language: i18n.t,
        navigationBarConfigs: {
            // Application logo
            homeLink: toPublicUrl('/Home'),
            logoLink: toPublicUrl("/assets/papyrus.png"),
            logoWidth: '9rem',
            alt: i18n.t('accessibility.papyruslogo'),
            // Authentication            
            authentication: {
                handleLogin: handleLogin,
                token: UserService.getKC().token,
                doLogin: UserService.doLogin,
                doLogout: UserService.doLogout,
                isAuthenticated: () => UserService.isAuthenticated(),
                getUserName: () => UserService.getUsername(),
            },
            // the menu items with their subItems who contains the navigation to the differents pages
            menuItems: [
                {
                    title: i18n.t('navbar.items.alldocuments'),
                    link: toPublicUrl("/AllDocuments"),
                    needsLogin: true,
                },
                {
                    title: i18n.t('navbar.items.bundledocuments'),
                    link: toPublicUrl("/BundleDocuments"),
                    needsLogin: true,
                },
                {
                    title: i18n.t('navbar.items.reader'),
                    link: toPublicUrl("/Reader"),
                    needsLogin: true,
                },
            ],
            // the user items 
            // Admin, Login, Logout are the default items
            dropDownItems: [],
        },
        // the title of the page
        titleKey: props.titleKey,
        // the loading state of the page
        loading: props.loading,
        // the content of the page
        children: props.children,
        // if the page needs login or not
        needsLogin: props.needsLogin,
        footerConfigs: {
            languages: {
                default: i18n.language,
                onChange: handleLangChange,
                options: [
                    {
                        label: "English",
                        value: "en"
                    },
                    {
                        label: "Français",
                        value: "fr"
                    }
                ]
            },
            logo: [
                {
                    src: "https://fyrstain.com/wp-content/uploads/2022/10/Logo_fyrstain_horyzontal.svg",
                    alt: i18n.t('accessibility.fyrstainlogo'),
                    href: "/Home"
                },
                ...(process.env.REACT_APP_DISPLAY_CLIENT_LOGO === 'true' ?
                    [
                        {
                            src: "https://fyrstain.com/wp-content/uploads/2022/10/Logo_fyrstain_horyzontal.svg",
                            alt: i18n.t('accessibility.fyrstainlogo'),
                            href: "/Home"
                        },
                        {
                            src: toPublicUrl("/assets/client_logo.jpg"),
                            alt: i18n.t('accessibility.clientlogo')
                        }
                    ]
                    : [])
            ],
            items: [
                {
                    label: i18n.t('footer.items.about'),
                    href: toPublicUrl("/InProgress"),
                },
                {
                    label: i18n.t('footer.items.contact'),
                    href: toPublicUrl("/InProgress"),
                },
                {
                    label: i18n.t('footer.items.problemtracking'),
                    href: toPublicUrl("/InProgress"),
                },
                {
                    label: i18n.t("footer.items.implementationguide"),
                    href: toPublicUrl("/ImplementationGuide"),
                },
            ]
        }
    };

    return <Page {...fullConfig} />;
};

export default PapyrusPage;
